import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileDown, DollarSign } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money, StatusBadge, ItemsEditor } from "./billingCommon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

export default function Invoices() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const { data: invoices } = useQuery({ queryKey: ["invoices"], queryFn: () => api.get("/invoices").then(r => r.data.invoices) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["invoices"] });
  const remove = async (inv) => {
    const paid = inv.amountPaid > 0 || ["paid", "partially_paid", "refunded"].includes(inv.status);
    try {
      await api.delete(`/invoices/${inv.id}${paid ? "?force=true" : ""}`); refresh(); toast.success("Invoice deleted");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Invoices" subtitle="Create invoices, record payments, and share branded PDFs" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setOpen(true)} data-testid="new-invoice-btn"><Plus className="h-4 w-4" /> New Invoice</Button>
      } />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Number</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Total</th><th className="text-left p-3">Balance</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {(invoices || []).length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No invoices yet.</td></tr> :
              (invoices || []).map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-3 font-mono text-xs text-slate-500">{inv.invoiceNumber}</td>
                  <td className="p-3 font-medium text-navy">{inv.customerName}<span className="block text-xs text-slate-400">{inv.customerEmail}</span></td>
                  <td className="p-3 text-navy">{money(inv.total)}</td>
                  <td className="p-3 font-medium text-navy">{money(inv.balance)}</td>
                  <td className="p-3"><StatusBadge status={inv.status} /></td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    {inv.balance > 0 && <button title="Record payment" onClick={() => setPayFor(inv)} className="p-1.5 rounded hover:bg-green-50 text-green-600" data-testid={`record-payment-${inv.id}`}><DollarSign className="h-4 w-4" /></button>}
                    <a href={`${API}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`invoice-pdf-${inv.id}`}><FileDown className="h-4 w-4" /></a>
                    {canDelete && (() => {
                      const paid = inv.amountPaid > 0 || ["paid", "partially_paid", "refunded"].includes(inv.status);
                      if (paid && !isOwner) return null;
                      return <ConfirmDelete onConfirm={() => remove(inv)} title={paid ? "Force delete this PAID invoice?" : "Delete this invoice?"} description={paid ? `Invoice ${inv.invoiceNumber} for ${inv.customerName} has recorded payments (${money(inv.amountPaid)}). Deleting it will also remove it from revenue reports. This is intended for cleaning up test data.` : `Invoice ${inv.invoiceNumber} for ${inv.customerName} will be removed.`} testid={`invoice-delete-${inv.id}`} />;
                    })()}
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <InvoiceDialog onClose={() => setOpen(false)} onSaved={() => { refresh(); setOpen(false); }} />}
      {payFor && <PaymentDialog invoice={payFor} onClose={() => setPayFor(null)} onSaved={() => { refresh(); setPayFor(null); }} />}
    </div>
  );
}

function InvoiceDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ customerName: "", customerEmail: "", taxRate: 0, discount: 0, notes: "" });
  const [items, setItems] = useState([{ name: "", description: "", quantity: 1, unitPrice: 0 }]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/invoices", { ...form, taxRate: Number(form.taxRate), discount: Number(form.discount), items: items.filter(i => i.name) }); toast.success("Invoice created"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const subtotal = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="invoice-form">
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Customer name" required value={form.customerName} onChange={e => set("customerName", e.target.value)} data-testid="inv-name" />
          <input className={input} type="email" placeholder="Customer email" required value={form.customerEmail} onChange={e => set("customerEmail", e.target.value)} data-testid="inv-email" />
        </div>
        <ItemsEditor items={items} setItems={setItems} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Tax %<input className={input} type="number" value={form.taxRate} onChange={e => set("taxRate", e.target.value)} data-testid="inv-tax" /></label>
          <label className="text-sm text-slate-600">Discount $<input className={input} type="number" value={form.discount} onChange={e => set("discount", e.target.value)} /></label>
        </div>
        <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Notes" value={form.notes} onChange={e => set("notes", e.target.value)} />
        <p className="text-sm text-slate-500 text-right">Subtotal: <span className="font-semibold text-navy">{money(subtotal)}</span></p>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="inv-save">Create Invoice</Button>
      </form>
    </DialogContent></Dialog>
  );
}

function PaymentDialog({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: invoice.balance, method: "cash", reference: "", notes: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/invoices/${invoice.id}/payments`, { ...form, amount: Number(form.amount) }); toast.success("Payment recorded"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Record Payment — {invoice.invoiceNumber}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="payment-form">
        <p className="text-sm text-slate-500">Balance due: <span className="font-semibold text-navy">{money(invoice.balance)}</span></p>
        <label className="text-sm text-slate-600 block">Amount<input className={input} type="number" step="0.01" required value={form.amount} onChange={e => set("amount", e.target.value)} data-testid="pay-amount" /></label>
        <label className="text-sm text-slate-600 block">Method<select className={input} value={form.method} onChange={e => set("method", e.target.value)} data-testid="pay-method">{["cash", "check", "money_order", "card", "other"].map(m => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select></label>
        <input className={input} placeholder="Reference (optional)" value={form.reference} onChange={e => set("reference", e.target.value)} />
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="pay-save">Record Payment</Button>
      </form>
    </DialogContent></Dialog>
  );
}
